import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import {
  addToCart,
  removeFromCart,
  clearCart,
} from "../redux/slices/cartSlice";

/**
 * Custom React hook for interacting with the shopping cart state.
 *
 * Provides access to the cart's ID, items, loading and error states,
 * and exposes functions to add, remove, and clear items in the cart.
 *
 * @returns An object containing:
 * - `cartId`: The current cart's unique identifier.
 * - `items`: The list of items in the cart.
 * - `loading`: Boolean indicating if cart operations are in progress.
 * - `error`: Any error encountered during cart operations.
 * - `addItemToCart`: Function to add a product to the cart.
 * - `removeItemFromCart`: Function to remove a product from the cart.
 * - `clear`: Function to clear all items from the cart.
 */
export const useCart = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { cartId, items, loading, error } = useSelector((state: RootState) => state.cart);

  const addItemToCart = (productId: string, qty = 1) => {
    if (!cartId) return;
    dispatch(addToCart({ cartId, productId, qty }));
  };

  const removeItemFromCart = (productId: string) => {
    if (!cartId) return;
    dispatch(removeFromCart({ cartId, productIds: [productId] }));
  };

  const clear = () => {
    if (!cartId) return;
    dispatch(clearCart());
  };

  return {
    cartId,
    items,
    loading,
    error,
    addItemToCart,
    removeItemFromCart,
    clear,
  };
};
